import type { FC } from 'react';
import { useRef, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { sanitizeHref, Url } from '@wix/da-url';
import Slider from '@wix/da-ds/pkg/Slider';
import PostCompactCard from '@wix/da-shared-react/pkg/PostCompactCard';

import s from './DywPostsSlider.scss';

export interface Props {
  posts: PapiDeviation[];
  className?: string;
  viewAllHref?: string;
  minItemsToShowViewAll?: number;
  /** Username to include in post URLs. When set, links go to /watch/{username}/posts?deviationid=XXX */
  username?: string;
}

const DywPostsSlider: FC<Props> = ({
  posts,
  className,
  viewAllHref,
  minItemsToShowViewAll = 9,
  username,
}) => {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLDivElement>(null);

  const getPostUrl = useCallback(
    (post: PapiDeviation) => {
      // When username is provided (specific user feed), link to /watch/{username}/posts?deviationid=XXX
      // When username is not provided (all/everyone feed), link to /watch/posts?deviationid=XXX
      return Url.browseDeviantsYouWatchLink('posts', username, post.deviationId);
    },
    [username]
  );

  if (!posts.length) {
    return null;
  }

  const sliderItems = posts.map(post => (
    <div key={post.deviationId} className={s['post-card']}>
      <a
        href={sanitizeHref(getPostUrl(post))}
        title={post.title}
        className={s['post-link']}
      >
        <PostCompactCard
          deviation={post}
          className={s['post-compact-card']}
          showUsername
        />
      </a>
    </div>
  ));

  if (viewAllHref && sliderItems.length >= minItemsToShowViewAll) {
    sliderItems.push(
      <a
        key="view-all"
        className={s['view-all-slide']}
        href={sanitizeHref(viewAllHref)}
      >
        {t('common.viewAll')}
      </a>
    );
  }

  return (
    <section className={classnames(s['root'], className)}>
      <div className={s['slider-wrapper']} ref={sliderRef}>
        <Slider className={s['slider']} contentClassName={s['slider-content']}>
          {sliderItems}
        </Slider>
      </div>
    </section>
  );
};

DywPostsSlider.displayName = 'DywPostsSlider';

export default DywPostsSlider;
